import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { reconcileTrialCardCapture, type TrialCardAddedConfirmation } from '@extension/wa-communication-layer/api/api-calls';
import { confirmationFromOutcome, isTrialCardReturn, withoutTrialCardReturn } from './trial-card-return-logic';

/**
 * #1229: on return from the Chargebee hosted page, ask the server to reconcile the pending consent
 * (the payment_source_added webhook may not have arrived). The server applies the same flag, allowlist
 * and guards as the webhook and answers `disabled` while the feature is off, so this is inert by default.
 * When the plan is activated the server sends the confirmation text (plan, price and first-charge date),
 * shown once so the customer knows the card worked, nothing was charged and does not add it twice.
 * A failure never blocks the app. The marker is removed from the URL first.
 */
export const TrialCardReturn: React.FC = () => {
  const { t } = useTranslation(namespace);
  const firedRef = useRef(false);
  const [confirmation, setConfirmation] = useState<TrialCardAddedConfirmation | null>(null);

  useEffect(() => {
    if (firedRef.current) return;
    const { search, pathname, hash } = window.location;
    if (!isTrialCardReturn(search)) return;
    firedRef.current = true;
    window.history.replaceState(window.history.state, '', withoutTrialCardReturn(search, pathname, hash));
    reconcileTrialCardCapture()
      .then((outcome) => setConfirmation(confirmationFromOutcome(outcome)))
      .catch(() => { /* the sweep covers a failed return call */ });
  }, []);

  if (confirmation === null) return null;
  return (
    <Dialog open onOpenChange={(next) => { if (!next) setConfirmation(null); }}>
      <DialogContent className="max-w-md" data-testid="trial-card-added">
        <DialogHeader>
          <DialogTitle>{confirmation.title}</DialogTitle>
          <DialogDescription>{confirmation.body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={() => setConfirmation(null)}>{t('common.ok')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
